import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Upload, Search, FileText, ExternalLink, Trash2, Sparkles, Link2, Clipboard, CheckCircle2, AlertCircle, Loader2, ChevronRight, X, Building2, User as UserIcon, ShieldCheck, Send, Settings as SettingsIcon } from "lucide-react";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

const STAGES = [
  { v: "BACKLOG", l: "Backlog", c: "bg-slate-100 text-slate-700 border-slate-300" },
  { v: "READY_FOR_DESIGN", l: "Ready for Design", c: "bg-blue-100 text-blue-700 border-blue-300" },
  { v: "IN_DESIGN", l: "In Design", c: "bg-indigo-100 text-indigo-700 border-indigo-300" },
  // REX-005 (Feb 2026): IN_REVIEW collapsed into PENDING_APPROVAL ("Awaiting Approval").
  // Legacy IN_REVIEW cards are still rendered if present, but no new lane.
  { v: "PENDING_APPROVAL", l: "Awaiting Approval", c: "bg-orange-100 text-orange-700 border-orange-300" },
  { v: "APPROVED", l: "Approved", c: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  { v: "SCHEDULED", l: "Scheduled", c: "bg-purple-100 text-purple-700 border-purple-300" },
  { v: "REJECTED", l: "Rejected", c: "bg-rose-100 text-rose-700 border-rose-300" },
  { v: "COMPLETED", l: "Completed", c: "bg-green-100 text-green-700 border-green-300" },
];

const CARD_TYPES = [
  { v: "SOCIAL_POST", l: "Social Post" },
  { v: "CAROUSEL", l: "Carousel" },
  { v: "BROCHURE", l: "Brochure" },
  { v: "PROPOSAL", l: "Proposal" },
  { v: "CLIENT_REPORT", l: "Client Report" },
  { v: "TRAINING_MATERIAL", l: "Training Material" },
  { v: "INTERNAL_ASSET", l: "Internal Asset" },
  { v: "OTHER", l: "Other" },
];

const CATEGORIES = [
  { v: "COMMERCIAL", l: "Commercial", c: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  { v: "CLIENT_DELIVERY", l: "Client Delivery", c: "bg-blue-100 text-blue-700 border-blue-300" },
  { v: "ADMIN_FINANCE", l: "Admin / Finance", c: "bg-slate-100 text-slate-700 border-slate-300" },
  { v: "INTERNAL", l: "Internal", c: "bg-purple-100 text-purple-700 border-purple-300" },
];

const REQUIRES_CLIENT = new Set(["COMMERCIAL", "CLIENT_DELIVERY"]);

async function apiCall(path, options = {}) {
  return fetch(`${API}${path}`, { credentials: "include", ...options });
}

export default function ContentProductionBoard({ user }) {
  const [cards, setCards] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({ total_uploads: 0, processed: 0, pending: 0, total_chunks: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ category: "", q: "", post_type: "company", production_path: "" });
  const [showNewCard, setShowNewCard] = useState(false);
  const [newCard, setNewCard] = useState({ title: "", description: "", card_type: "", category: "", post_type: "company", client_id: "", client_name: "" });
  const [detailCard, setDetailCard] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  // Approval gateway
  const [approverEmails, setApproverEmails] = useState([]);
  const [showApproverConfig, setShowApproverConfig] = useState(false);
  const [showBrandKit, setShowBrandKit] = useState(false);
  const userEmail = (user?.email || "").toLowerCase();
  const userIsApprover = approverEmails.includes(userEmail);
  const userIsAdmin = user?.role === "admin";

  const fetchApprovers = useCallback(async () => {
    const r = await apiCall("/content-studio/approvers");
    if (r.ok) {
      const data = await r.json();
      setApproverEmails(data.approver_emails || []);
    }
  }, []);
  useEffect(() => { fetchApprovers(); }, [fetchApprovers]);

  const fetchCards = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filter.category) params.set("category", filter.category);
    if (filter.q) params.set("q", filter.q);
    if (filter.post_type) params.set("post_type", filter.post_type);
    if (filter.production_path) params.set("production_path", filter.production_path);
    const [r, sR, cR] = await Promise.all([
      apiCall(`/content-studio/cards?${params.toString()}`),
      apiCall(`/content-studio/knowledge/stats`),
      apiCall(`/mission-control/client-health?limit=200`),
    ]);
    if (r.ok) setCards((await r.json()).cards || []);
    if (sR.ok) setStats(await sR.json());
    if (cR.ok) setClients((await cR.json()).clients || []);
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchCards(); }, [fetchCards]);

  const createCard = async () => {
    if (!newCard.title.trim() || !newCard.card_type || !newCard.category) {
      toast.error("Title, type, and category are required");
      return;
    }
    if (REQUIRES_CLIENT.has(newCard.category) && !newCard.client_id) {
      toast.error("Client is required for Commercial / Client Delivery");
      return;
    }
    const r = await apiCall("/content-studio/cards", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newCard),
    });
    if (r.ok) {
      toast.success("Card created");
      setShowNewCard(false);
      setNewCard({ title: "", description: "", card_type: "", category: "", post_type: filter.post_type || "company", client_id: "", client_name: "" });
      fetchCards();
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Failed");
    }
  };

  const moveStage = async (cardId, stage) => {
    // Optimistic update — move locally first so the UI feels instant
    const prev = cards;
    setCards(cs => cs.map(c => c.card_id === cardId ? { ...c, pipeline_stage: stage } : c));
    if (detailCard?.card_id === cardId) setDetailCard(c => ({ ...c, pipeline_stage: stage }));
    const r = await apiCall(`/content-studio/cards/${cardId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pipeline_stage: stage }),
    });
    if (r.ok) {
      toast.success(`Moved to ${STAGES.find(s => s.v === stage)?.l}`);
    } else {
      // Roll back on failure
      setCards(prev);
      toast.error("Failed to move card");
    }
  };

  // Drag-and-drop state
  const [draggingCardId, setDraggingCardId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  const onCardDragStart = (e, card) => {
    setDraggingCardId(card.card_id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", card.card_id);
  };
  const onCardDragEnd = () => { setDraggingCardId(null); setDragOverStage(null); };
  const onColDragOver = (e, stageV) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverStage !== stageV) setDragOverStage(stageV);
  };
  const onColDragLeave = (e, stageV) => {
    // Only clear if we actually leave the column (not entering a child element)
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (dragOverStage === stageV) setDragOverStage(null);
  };
  const onColDrop = (e, stageV) => {
    e.preventDefault();
    const cardId = e.dataTransfer.getData("text/plain") || draggingCardId;
    setDraggingCardId(null);
    setDragOverStage(null);
    if (!cardId) return;
    const card = cards.find(c => c.card_id === cardId);
    if (!card || card.pipeline_stage === stageV) return;
    moveStage(cardId, stageV);
  };

  const runSearch = async () => {
    if (!searchQ.trim()) return;
    setSearching(true);
    const r = await apiCall(`/content-studio/search?q=${encodeURIComponent(searchQ)}&top_k=5`);
    setSearching(false);
    if (r.ok) setSearchResults((await r.json()).results || []);
  };

  return (
    <div className="space-y-4" data-testid="content-production-board">
      {/* Voice/Energy Silo Tabs — Personal vs RESILIGENT must never share a board */}
      <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg w-fit" data-testid="post-type-tabs">
        <button
          onClick={() => setFilter(p => ({ ...p, post_type: "company" }))}
          data-testid="tab-company"
          className={`px-4 py-1.5 rounded-md text-sm font-semibold transition ${filter.post_type === "company" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        >
          <Building2 className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5"/>RESILIGENT
        </button>
        <button
          onClick={() => setFilter(p => ({ ...p, post_type: "personal" }))}
          data-testid="tab-personal"
          className={`px-4 py-1.5 rounded-md text-sm font-semibold transition ${filter.post_type === "personal" ? "bg-white text-purple-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        >
          <UserIcon className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5"/>Personal / Founder
        </button>
      </div>

      {/* Production Path filter pills */}
      <div className="flex items-center gap-1.5 text-[11px]">
        <span className="text-slate-500 font-semibold mr-1">Path:</span>
        {[
          { v: "", l: "All", c: "border-slate-300 text-slate-600" },
          { v: "auto_rex", l: "🤖 Auto-Rex", c: "border-amber-300 text-amber-700 bg-amber-50" },
          { v: "designer_required", l: "🎨 Designer", c: "border-rose-300 text-rose-700 bg-rose-50" },
          { v: "pending", l: "⏳ Pending", c: "border-slate-300 text-slate-500 bg-slate-50" },
        ].map(o => (
          <button key={o.v || "all"} onClick={() => setFilter(p => ({ ...p, production_path: o.v }))}
            data-testid={`path-filter-${o.v || "all"}`}
            className={`px-2 py-0.5 rounded-full border font-semibold transition ${filter.production_path === o.v ? o.c + " ring-1 ring-offset-1 ring-current" : "border-slate-200 text-slate-400"}`}>
            {o.l}
          </button>
        ))}
      </div>

      {/* Stats + Search Bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-4 text-xs text-slate-600">
          <span><span className="font-bold text-slate-900">{cards.length}</span> cards</span>
          <span><span className="font-bold text-emerald-700">{stats.processed}</span> processed</span>
          <span><span className="font-bold text-amber-600">{stats.pending}</span> pending</span>
          <span><span className="font-bold text-indigo-700">{stats.total_chunks}</span> knowledge chunks</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Select value={filter.category || "ALL"} onValueChange={v => setFilter(p => ({ ...p, category: v === "ALL" ? "" : v }))}>
            <SelectTrigger className="w-40 h-8 text-xs"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All categories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input className="w-48 h-8 text-xs" placeholder="Filter by title/client..." value={filter.q} onChange={e => setFilter(p => ({ ...p, q: e.target.value }))} data-testid="cs-filter-input" />
          <Button size="sm" variant="outline" className="text-xs" onClick={() => setShowApproverConfig(true)} data-testid="approver-config-btn" title="Configure approvers">
            <ShieldCheck className="h-3.5 w-3.5 mr-1"/>Approvers ({approverEmails.length})
          </Button>
          <Button size="sm" variant="outline" className="text-xs" onClick={() => setShowBrandKit(true)} data-testid="brand-kit-btn" title="Brand kit for auto-render">
            <SettingsIcon className="h-3.5 w-3.5 mr-1"/>Brand Kit
          </Button>
          <Button size="sm" data-testid="new-card-btn" onClick={() => setShowNewCard(true)}><Plus className="h-4 w-4 mr-1" /> New Card</Button>
        </div>
      </div>

      {/* Semantic Search Bar */}
      <div className="rounded-lg border-2 border-dashed border-indigo-200 bg-indigo-50/50 p-3">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-indigo-600" />
          <Input className="h-8 text-sm bg-white" placeholder="Search REX knowledge (semantic) — e.g. 'leadership report for Nigerian bank'"
            value={searchQ} onChange={e => setSearchQ(e.target.value)}
            onKeyDown={e => e.key === "Enter" && runSearch()}
            data-testid="knowledge-search-input" />
          <Button size="sm" disabled={searching || !searchQ.trim()} onClick={runSearch} data-testid="knowledge-search-btn">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
          </Button>
          {searchResults.length > 0 && <Button size="sm" variant="ghost" onClick={() => { setSearchResults([]); setSearchQ(""); }}><X className="h-4 w-4" /></Button>}
        </div>
        {searchResults.length > 0 && (
          <div className="mt-3 space-y-2" data-testid="knowledge-search-results">
            <p className="text-[11px] text-indigo-700 font-semibold uppercase tracking-wider">Knowledge matches</p>
            {searchResults.map((r, i) => (
              <div key={i} className="bg-white rounded border border-indigo-100 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <FileText className="h-3.5 w-3.5 text-indigo-500" />
                    <span className="text-sm font-semibold text-slate-900 truncate">{r.filename}</span>
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">{Math.round((r.score || 0) * 100)}%</span>
                    {r.tags?.content_type && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{r.tags.content_type}</span>}
                    {r.tags?.topic && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{r.tags.topic}</span>}
                  </div>
                  {r.card_id && <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => {
                    const card = cards.find(c => c.card_id === r.card_id);
                    if (card) setDetailCard(card);
                  }}>Open Card</Button>}
                </div>
                <p className="text-xs text-slate-600 mt-1 line-clamp-2">{r.chunk}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pipeline — 8 columns (incl. Pending Approval gate) */}
      {loading ? <div className="text-center py-8 text-slate-400">Loading...</div> : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          {STAGES.map(stage => {
            const col = cards.filter(c => c.pipeline_stage === stage.v);
            const isDragOver = dragOverStage === stage.v;
            return (
              <div
                key={stage.v}
                className={`rounded-lg border-2 ${stage.c} bg-white min-h-[200px] transition-all ${isDragOver ? "ring-2 ring-offset-1 ring-indigo-500 scale-[1.01]" : ""}`}
                data-testid={`stage-col-${stage.v}`}
                onDragOver={e => onColDragOver(e, stage.v)}
                onDragLeave={e => onColDragLeave(e, stage.v)}
                onDrop={e => onColDrop(e, stage.v)}
              >
                <div className={`px-2 py-1.5 border-b-2 ${stage.c} rounded-t-[6px]`}>
                  <p className="text-[11px] font-bold uppercase tracking-wider">{stage.l}</p>
                  <p className="text-[10px] opacity-70">{col.length}</p>
                </div>
                <div className="p-2 space-y-1.5">
                  {col.length === 0 && <p className={`text-[10px] text-center py-3 transition-colors ${isDragOver ? "text-indigo-500 font-semibold" : "text-slate-300"}`}>{isDragOver ? "Drop here" : "Empty"}</p>}
                  {col.map(c => {
                    const cat = CATEGORIES.find(x => x.v === c.category);
                    const isDragging = draggingCardId === c.card_id;
                    const isPersonal = (c.post_type || "company") === "personal";
                    return (
                      <div
                        key={c.card_id}
                        draggable
                        onDragStart={e => onCardDragStart(e, c)}
                        onDragEnd={onCardDragEnd}
                        className={`bg-white rounded border ${isPersonal ? "border-l-[3px] border-l-purple-500 border-slate-200" : "border-l-[3px] border-l-blue-500 border-slate-200"} p-2 hover:shadow-md hover:border-slate-300 transition cursor-grab active:cursor-grabbing ${isDragging ? "opacity-40" : ""}`}
                        data-testid={`card-${c.card_id}`}
                        onClick={() => setDetailCard(c)}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="flex items-center gap-1 mb-1">
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${isPersonal ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>
                            {isPersonal ? "PERSONAL" : "RESILIGENT"}
                          </span>
                          {c.production_path === "auto_rex" && (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 inline-flex items-center gap-0.5" title="Auto-rendered by Rex (no designer needed)">
                              <Sparkles className="h-2.5 w-2.5"/>AUTO-REX
                            </span>
                          )}
                          {c.production_path === "designer_required" && (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 inline-flex items-center gap-0.5" title="Needs human designer">
                              <UserIcon className="h-2.5 w-2.5"/>DESIGNER
                            </span>
                          )}
                          {c.production_path === "pending" && (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500" title="Classification pending">
                              ⏳
                            </span>
                          )}
                        </div>
                        <p className="text-xs font-semibold text-slate-900 line-clamp-2">{c.title}</p>
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          {cat && <span className={`text-[9px] px-1 py-0.5 rounded ${cat.c}`}>{cat.l}</span>}
                          <span className="text-[9px] px-1 py-0.5 rounded bg-slate-50 text-slate-500 border border-slate-200">{CARD_TYPES.find(t => t.v === c.card_type)?.l || c.card_type}</span>
                        </div>
                        {c.client_name && <p className="text-[10px] text-blue-600 mt-1 truncate">{c.client_name}</p>}
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400">
                          {c.upload_count > 0 && <span className="flex items-center gap-0.5"><Upload className="h-2.5 w-2.5" />{c.upload_count}</span>}
                          {c.processed_upload_count > 0 && <span className="flex items-center gap-0.5 text-emerald-600"><CheckCircle2 className="h-2.5 w-2.5" />{c.processed_upload_count}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New Card Dialog */}
      <Dialog open={showNewCard} onOpenChange={setShowNewCard}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="new-card-dialog">
          <DialogHeader><DialogTitle>New Content Card</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs mb-1.5 block">Voice <span className="text-red-500">*</span></Label>
              <div className="grid grid-cols-2 gap-2" data-testid="new-card-post-type">
                <button type="button" data-testid="new-card-post-type-company"
                  className={`text-xs font-semibold px-3 py-1.5 rounded border-2 transition ${newCard.post_type === "company" ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-white text-slate-500 border-slate-200"}`}
                  onClick={() => setNewCard(p => ({ ...p, post_type: "company" }))}>RESILIGENT</button>
                <button type="button" data-testid="new-card-post-type-personal"
                  className={`text-xs font-semibold px-3 py-1.5 rounded border-2 transition ${newCard.post_type === "personal" ? "bg-purple-100 text-purple-700 border-purple-300" : "bg-white text-slate-500 border-slate-200"}`}
                  onClick={() => setNewCard(p => ({ ...p, post_type: "personal" }))}>Personal / Founder</button>
              </div>
            </div>
            <div><Label className="text-xs">Title <span className="text-red-500">*</span></Label>
              <Input value={newCard.title} onChange={e => setNewCard(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Q3 Leadership report for Zenith Bank" data-testid="new-card-title" /></div>
            <div>
              <Label className="text-xs mb-1.5 block">Type <span className="text-red-500">*</span></Label>
              <div className="grid grid-cols-2 gap-1.5" data-testid="new-card-type">
                {CARD_TYPES.map(t => (
                  <button key={t.v} type="button" data-testid={`new-card-type-${t.v}`}
                    className={`text-[11px] font-semibold px-2 py-1.5 rounded border-2 transition ${newCard.card_type === t.v ? "bg-indigo-100 text-indigo-700 border-indigo-300" : "bg-white text-slate-500 border-slate-200"}`}
                    onClick={() => setNewCard(p => ({ ...p, card_type: t.v }))}>{t.l}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs mb-1.5 block">Category <span className="text-red-500">*</span></Label>
              <div className="grid grid-cols-2 gap-2" data-testid="new-card-category">
                {CATEGORIES.map(c => (
                  <button key={c.v} type="button" data-testid={`new-card-cat-${c.v}`}
                    className={`text-xs font-semibold px-3 py-1.5 rounded border-2 transition ${newCard.category === c.v ? c.c : "bg-white text-slate-500 border-slate-200"}`}
                    onClick={() => setNewCard(p => ({ ...p, category: c.v }))}>{c.l}</button>
                ))}
              </div>
            </div>
            {REQUIRES_CLIENT.has(newCard.category) && (
              <div>
                <Label className="text-xs">Client <span className="text-red-500">*</span></Label>
                <Input value={newCard.client_name} onChange={e => {
                  const val = e.target.value;
                  const match = clients.find(c => c.company_name?.toLowerCase() === val.toLowerCase());
                  setNewCard(p => ({ ...p, client_name: val, client_id: match?.client_id || "" }));
                }} placeholder="Type client name..." list="new-card-client-list" data-testid="new-card-client" />
                <datalist id="new-card-client-list">
                  {clients.map(c => <option key={c.client_id} value={c.company_name} />)}
                </datalist>
                {newCard.client_name && !newCard.client_id && <p className="text-[10px] text-amber-600 mt-1">Pick from dropdown to link</p>}
              </div>
            )}
            <div><Label className="text-xs">Description (optional)</Label>
              <Textarea value={newCard.description} onChange={e => setNewCard(p => ({ ...p, description: e.target.value }))} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCard(false)}>Cancel</Button>
            <Button data-testid="new-card-save" disabled={!newCard.title.trim() || !newCard.card_type || !newCard.category || (REQUIRES_CLIENT.has(newCard.category) && !newCard.client_id)} onClick={createCard}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Card Detail Dialog */}
      <CardDetail
        detailCard={detailCard}
        setDetailCard={setDetailCard}
        moveStage={moveStage}
        onRefresh={fetchCards}
        approverEmails={approverEmails}
        userIsApprover={userIsApprover}
      />

      {/* Approver Configuration Dialog */}
      <ApproverConfigDialog
        open={showApproverConfig}
        onClose={() => setShowApproverConfig(false)}
        approverEmails={approverEmails}
        setApproverEmails={setApproverEmails}
        userIsAdmin={userIsAdmin}
      />

      {/* Brand Kit Dialog (Phase 3) */}
      <BrandKitDialog
        open={showBrandKit}
        onClose={() => setShowBrandKit(false)}
        userIsAdmin={userIsAdmin}
      />
    </div>
  );
}


function CardDetail({ detailCard, setDetailCard, moveStage, onRefresh, approverEmails, userIsApprover }) {
  const [tab, setTab] = useState("assets");
  const [pasteTxt, setPasteTxt] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [fullCard, setFullCard] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [requestingApproval, setRequestingApproval] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  // Designer assets
  const [assets, setAssets] = useState([]);
  const [uploadingAsset, setUploadingAsset] = useState(false);
  const [lightboxAsset, setLightboxAsset] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  // Phase 3 — Auto-render state must be declared before any conditional return
  const [autoRendering, setAutoRendering] = useState(false);

  const refresh = useCallback(async () => {
    if (!detailCard) return;
    const [r, ar] = await Promise.all([
      apiCall(`/content-studio/cards/${detailCard.card_id}`),
      apiCall(`/content-studio/cards/${detailCard.card_id}/assets`),
    ]);
    if (r.ok) setFullCard(await r.json());
    if (ar.ok) {
      const list = (await ar.json()).assets || [];
      // Sort: non-carousel by uploaded_at desc, carousel slides stay grouped by seq asc
      list.sort((a, b) => {
        const aIsCarousel = a.asset_type === "carousel";
        const bIsCarousel = b.asset_type === "carousel";
        if (aIsCarousel && bIsCarousel) return (a.carousel_seq || 0) - (b.carousel_seq || 0);
        if (aIsCarousel !== bIsCarousel) return aIsCarousel ? 1 : -1;
        return (b.uploaded_at || "").localeCompare(a.uploaded_at || "");
      });
      setAssets(list);
    }
  }, [detailCard]);

  useEffect(() => { refresh(); }, [refresh]);

  const requestApproval = async () => {
    if (!detailCard || (approverEmails || []).length === 0) {
      toast.error("No approvers configured. Click 'Approvers' in the toolbar to set them.");
      return;
    }
    setRequestingApproval(true);
    const r = await apiCall(`/content-studio/cards/${detailCard.card_id}/request-approval`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
    setRequestingApproval(false);
    if (r.ok) {
      const data = await r.json();
      if (data.email?.sent) {
        toast.success(`Approval requested — emailed to ${data.approver_emails.length} approver(s).`);
      } else {
        toast.warning("Moved to Pending Approval, but email could not be sent. " + (data.email?.error || ""));
      }
      refresh();
      onRefresh();
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Failed to request approval");
    }
  };

  const approveInApp = async () => {
    if (!detailCard) return;
    setApproving(true);
    const r = await apiCall(`/content-studio/cards/${detailCard.card_id}/approve`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
    setApproving(false);
    if (r.ok) {
      toast.success("Approved");
      refresh();
      onRefresh();
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Failed to approve");
    }
  };

  const rejectInApp = async () => {
    if (!detailCard || rejectReason.trim().length < 3) {
      toast.error("Please provide a rejection reason (min 3 chars)");
      return;
    }
    setRejecting(true);    const r = await apiCall(`/content-studio/cards/${detailCard.card_id}/reject`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reason: rejectReason }) });
    setRejecting(false);
    if (r.ok) {
      toast.success("Rejected — designer notified for rework");
      setShowRejectModal(false);
      setRejectReason("");
      refresh();
      onRefresh();
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Failed to reject");
    }
  };

  if (!detailCard) return null;

  const uploadAsset = async (file) => {
    if (!file) return;
    setUploadingAsset(true);
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch(`${API}/content-studio/cards/${detailCard.card_id}/assets`, {
      method: "POST", credentials: "include", body: fd,
    });
    setUploadingAsset(false);
    if (r.ok) {
      toast.success("Asset attached");
      refresh();
      onRefresh();
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Upload failed");
    }
  };

  const deleteAsset = async (assetId) => {
    if (!confirm("Remove this asset from the card?")) return;
    const r = await apiCall(`/content-studio/assets/${assetId}`, { method: "DELETE" });
    if (r.ok) {
      toast.success("Asset removed");
      refresh();
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Failed");
    }
  };

  const generateUploadLink = async () => {
    setGeneratingLink(true);
    const r = await apiCall(`/content-studio/cards/${detailCard.card_id}/upload-link`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ expires_hours: 168 }),
    });
    setGeneratingLink(false);
    if (r.ok) {
      const data = await r.json();
      try {
        await navigator.clipboard.writeText(data.upload_url);
        toast.success("Designer upload link copied to clipboard");
      } catch {
        toast.success(`Upload link: ${data.upload_url}`);
      }
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Failed");
    }
  };

  // Phase 2 — Production Path actions
  const reclassifyPath = async () => {
    const r = await apiCall(`/content-studio/cards/${detailCard.card_id}/classify-production-path`, { method: "POST" });
    if (r.ok) {
      toast.success("Re-classified");
      refresh(); onRefresh();
    } else {
      toast.error("Re-classification failed");
    }
  };

  const overridePath = async (target) => {
    const note = prompt(`Override to "${target}". Optional note (why?):`) || "";
    const r = await apiCall(`/content-studio/cards/${detailCard.card_id}/override-production-path`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ production_path: target, note }),
    });
    if (r.ok) {
      toast.success(`Routed to ${target === "auto_rex" ? "Auto-Rex" : "Designer"}`);
      refresh(); onRefresh();
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Override failed");
    }
  };

  // Phase 3 — Auto-render
  const autoRender = async (strategyOverride = "") => {
    setAutoRendering(true);
    const r = await apiCall(`/content-studio/cards/${detailCard.card_id}/auto-render`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(strategyOverride ? { strategy: strategyOverride } : {}),
    });
    setAutoRendering(false);
    if (r.ok) {
      const data = await r.json();
      if (data.carousel_assets && data.carousel_assets.length > 0) {
        toast.success(`Rex generated a ${data.carousel_assets.length}-slide carousel — all slides attached`);
      } else {
        toast.success(`Rex rendered a ${data.asset_type.replace("_", " ")} — attached to card`);
      }
      refresh(); onRefresh();
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Render failed");
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("include_in_knowledge", "true");
    const r = await fetch(`${API}/content-studio/cards/${detailCard.card_id}/upload-file`, {
      method: "POST", credentials: "include", body: fd,
    });
    setUploading(false);
    if (r.ok) {
      toast.success("File uploaded — extracting in background");
      setTimeout(() => { refresh(); onRefresh(); }, 800);
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Upload failed");
    }
  };

  const pasteTextUpload = async () => {
    if (!pasteTxt.trim()) return;
    const r = await apiCall(`/content-studio/cards/${detailCard.card_id}/paste-text`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: pasteTxt, title: `Note ${new Date().toLocaleDateString()}` }),
    });
    if (r.ok) {
      toast.success("Text added — REX is learning from it");
      setPasteTxt("");
      setTimeout(() => { refresh(); onRefresh(); }, 800);
    }
  };

  const addLink = async () => {
    if (!linkUrl.trim()) return;
    const r = await apiCall(`/content-studio/cards/${detailCard.card_id}/add-link`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ url: linkUrl }),
    });
    if (r.ok) {
      toast.success("Link added");
      setLinkUrl("");
      refresh();
    }
  };

  const deleteUpload = async (upId) => {
    await apiCall(`/content-studio/uploads/${upId}`, { method: "DELETE" });
    toast.success("Removed");
    refresh();
  };

  const reprocess = async (upId) => {
    await apiCall(`/content-studio/uploads/${upId}/reprocess`, { method: "POST" });
    toast.info("Re-processing");
    setTimeout(refresh, 1500);
  };

  const currentStage = STAGES.find(s => s.v === (fullCard?.pipeline_stage || detailCard.pipeline_stage));
  const cat = CATEGORIES.find(c => c.v === (fullCard?.category || detailCard.category));
  const uploads = fullCard?.uploads || [];

  return (
    <Dialog open={!!detailCard} onOpenChange={() => { setDetailCard(null); setFullCard(null); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="card-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {detailCard.title}
            {((fullCard?.post_type || detailCard.post_type || "company") === "personal") ? (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-purple-100 text-purple-700">PERSONAL</span>
            ) : (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-700">RESILIGENT</span>
            )}
            {cat && <span className={`text-[10px] px-2 py-0.5 rounded ${cat.c}`}>{cat.l}</span>}
            {currentStage && <span className={`text-[10px] px-2 py-0.5 rounded ${currentStage.c}`}>{currentStage.l}</span>}
          </DialogTitle>
        </DialogHeader>

        {/* PRODUCTION PATH PANEL (Phase 2) */}
        {(() => {
          const path = fullCard?.production_path || detailCard.production_path || "pending";
          const cls = fullCard?.path_classification || detailCard.path_classification || {};
          const isAuto = path === "auto_rex";
          const isDesigner = path === "designer_required";
          return (
            <div className={`rounded-lg border ${isAuto ? "border-amber-200 bg-amber-50/60" : isDesigner ? "border-rose-200 bg-rose-50/60" : "border-slate-200 bg-slate-50"} p-3`} data-testid="production-path-panel">
              <div className="flex items-start gap-3">
                {isAuto ? <Sparkles className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" /> : isDesigner ? <UserIcon className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5"/> : <Loader2 className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5"/>}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${isAuto ? "text-amber-900" : isDesigner ? "text-rose-900" : "text-slate-700"}`}>
                    {isAuto && "Rex will auto-render this"}
                    {isDesigner && "Routed to designer"}
                    {!isAuto && !isDesigner && "Classification pending..."}
                    {cls.manual_override && <span className="ml-2 text-[9px] uppercase tracking-wider bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">Manual override</span>}
                  </p>
                  {cls.reason && <p className="text-[11px] text-slate-600 mt-0.5">{cls.reason}</p>}
                  <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-1.5">
                    {cls.asset_strategy && <span>Strategy: <strong className="text-slate-700">{cls.asset_strategy.replace(/_/g," ")}</strong></span>}
                    {cls.confidence !== undefined && <span>Confidence: <strong className="text-slate-700">{cls.confidence}%</strong></span>}
                    {cls.classified_by && <span className="text-slate-400">via {cls.classified_by}</span>}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  {isAuto && (
                    <div className="flex items-center gap-1">
                      <Button size="sm" className="text-[10px] h-6 bg-amber-600 hover:bg-amber-700 text-white" onClick={() => autoRender("")} disabled={autoRendering} data-testid="auto-render-btn">
                        {autoRendering ? <Loader2 className="h-2.5 w-2.5 animate-spin"/> : <><Sparkles className="h-2.5 w-2.5 mr-1"/>Auto-Render</>}
                      </Button>
                      <select className="text-[9px] h-6 rounded border border-amber-200 bg-white text-amber-700" onChange={(e) => e.target.value && autoRender(e.target.value)} disabled={autoRendering} data-testid="auto-render-strategy-select" defaultValue="">
                        <option value="">as</option>
                        <option value="quote_card">Quote card</option>
                        <option value="stat_card">Stat card</option>
                        <option value="text_only">Text post</option>
                        <option value="simple_carousel">Carousel (Claude)</option>
                        <option value="branded_hero">Hero image (Nano Banana)</option>
                      </select>
                    </div>
                  )}
                  {!isAuto && (
                    <Button size="sm" variant="outline" className="text-[10px] h-6 border-amber-300 text-amber-700 hover:bg-amber-100" onClick={() => overridePath("auto_rex")} data-testid="override-to-auto-btn">
                      <Sparkles className="h-2.5 w-2.5 mr-1"/>Send to Rex
                    </Button>
                  )}
                  {!isDesigner && (
                    <Button size="sm" variant="outline" className="text-[10px] h-6 border-rose-300 text-rose-700 hover:bg-rose-100" onClick={() => overridePath("designer_required")} data-testid="override-to-designer-btn">
                      <UserIcon className="h-2.5 w-2.5 mr-1"/>Send to Designer
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-[10px] h-6 text-slate-500" onClick={reclassifyPath} data-testid="reclassify-btn">
                    Re-analyze
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Stage controls */}
        <div className="flex items-center gap-1.5 flex-wrap py-2 border-y">
          <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Move to:</span>
          {STAGES.filter(s => s.v !== (fullCard?.pipeline_stage || detailCard.pipeline_stage)).map(s => (
            <button key={s.v} className={`text-[10px] font-semibold px-2 py-1 rounded border transition hover:scale-105 ${s.c}`}
              data-testid={`move-to-${s.v}`}
              onClick={() => moveStage(detailCard.card_id, s.v)}>{s.l}</button>
          ))}
        </div>

        {/* APPROVAL GATEWAY PANEL */}
        {(() => {
          const stage = fullCard?.pipeline_stage || detailCard.pipeline_stage;
          const status = fullCard?.approval_status || detailCard.approval_status || "none";
          const hasApprovers = (approverEmails || []).length > 0;

          // REX-005: From IN_DESIGN (or legacy IN_REVIEW): "Submit for Approval" CTA.
          // Designer completes work → moves directly to PENDING_APPROVAL (no IN_REVIEW interstitial).
          if (stage === "IN_DESIGN" || stage === "IN_REVIEW") {
            return (
              <div className="rounded-lg border-2 border-orange-200 bg-orange-50/60 p-3" data-testid="request-approval-panel">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-orange-900">Ready for sign-off?</p>
                    <p className="text-[11px] text-orange-700/80 mt-0.5">Sends a one-click approval email to: {hasApprovers ? approverEmails.join(", ") : "no approvers configured yet"}</p>
                  </div>
                  <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white" onClick={requestApproval} disabled={requestingApproval || !hasApprovers} data-testid="request-approval-btn">
                    {requestingApproval ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" />Submit for Approval</>}
                  </Button>
                </div>
              </div>
            );
          }

          // PENDING_APPROVAL: show approver actions or waiting state
          if (stage === "PENDING_APPROVAL") {
            const requester = fullCard?.approval_requested_by_name || fullCard?.approval_requested_by || "—";
            const requestedAt = fullCard?.approval_requested_at;
            return (
              <div className="rounded-lg border-2 border-orange-300 bg-orange-50 p-3" data-testid="pending-approval-panel">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-200">
                    <ShieldCheck className="h-3.5 w-3.5 text-orange-700" />
                  </span>
                  <p className="text-sm font-semibold text-orange-900">Awaiting Approval</p>
                  <span className="text-[10px] text-orange-700/80">requested by {requester}{requestedAt ? ` • ${new Date(requestedAt).toLocaleString("en-GB", {day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}` : ""}</span>
                </div>
                {userIsApprover ? (
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={approveInApp} disabled={approving} data-testid="approve-card-btn">
                      {approving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCircle2 className="h-3.5 w-3.5 mr-1"/>Approve</>}
                    </Button>
                    <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => setShowRejectModal(true)} data-testid="reject-card-btn">
                      <X className="h-3.5 w-3.5 mr-1"/>Reject (rework)
                    </Button>
                    <Button size="sm" variant="ghost" className="text-orange-700" onClick={requestApproval} disabled={requestingApproval} title="Resend approval email">
                      <Send className="h-3.5 w-3.5"/>
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-orange-800/80">Only configured approvers ({approverEmails.join(", ") || "none"}) can decide. They were emailed a one-click approve/reject link.</p>
                )}
              </div>
            );
          }

          // Approved/Rejected — show audit
          if (status === "approved") {
            return (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2.5 text-xs text-emerald-800 flex items-center gap-2" data-testid="approved-banner">
                <CheckCircle2 className="h-4 w-4"/>
                <span>Approved by <strong>{fullCard?.approved_by_name || fullCard?.approved_by || "—"}</strong>{fullCard?.approved_at ? ` • ${new Date(fullCard.approved_at).toLocaleString("en-GB", {day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}` : ""}</span>
              </div>
            );
          }
          if (status === "rejected") {
            return (
              <div className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs" data-testid="rejected-banner">
                <div className="flex items-center gap-2 text-red-800">
                  <AlertCircle className="h-4 w-4"/>
                  <span>Rework requested by <strong>{fullCard?.rejected_by_name || fullCard?.rejected_by || "—"}</strong></span>
                </div>
                {fullCard?.rejection_reason && <p className="mt-1 text-red-700 italic">"{fullCard.rejection_reason}"</p>}
              </div>
            );
          }
          return null;
        })()}

        {/* Tabs */}
        <div className="flex gap-1 border-b">
          {[{v:"assets", l:`Assets (${assets.length})`}, {v:"uploads", l:`Knowledge (${uploads.length})`}, {v:"add", l:"Add Content"}].map(t => (
            <button key={t.v} className={`text-xs font-semibold px-3 py-1.5 ${tab === t.v ? "border-b-2 border-indigo-500 text-indigo-700" : "text-slate-500"}`} onClick={() => setTab(t.v)} data-testid={`tab-${t.v}`}>
              {t.l}
            </button>
          ))}
        </div>

        {tab === "assets" && (
          <div className="space-y-3" data-testid="assets-tab">
            <div className="flex items-center gap-2">
              <input type="file" id="asset-upload-input" className="hidden" data-testid="asset-upload-input"
                accept=".png,.jpg,.jpeg,.webp,.gif,.pdf,.mp4,.mov" onChange={e => e.target.files?.[0] && uploadAsset(e.target.files[0])} />
              <label htmlFor="asset-upload-input">
                <Button size="sm" asChild disabled={uploadingAsset} data-testid="upload-asset-btn">
                  <span>{uploadingAsset ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Uploading...</> : <><Upload className="h-3 w-3 mr-1"/>Upload Asset</>}</span>
                </Button>
              </label>
              <Button size="sm" variant="outline" onClick={generateUploadLink} disabled={generatingLink} data-testid="generate-upload-link-btn" title="Generate a tokenized upload link for the designer (no login required)">
                {generatingLink ? <Loader2 className="h-3 w-3 animate-spin"/> : <><Link2 className="h-3 w-3 mr-1"/>Designer Link</>}
              </Button>
              <span className="text-[10px] text-slate-400 ml-auto">PNG · JPG · PDF · MP4 · 25MB max</span>
            </div>
            {assets.length === 0 ? (
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center text-xs text-slate-400">
                No artwork attached yet. Upload via this dialog, or generate a designer upload link to share.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {assets.map(a => (
                  <div key={a.asset_id} className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden group relative" data-testid={`asset-${a.asset_id}`}>
                    {a.mime_type?.startsWith("image/") ? (
                      <img src={a.public_url} alt={a.filename} className="w-full h-32 object-cover cursor-zoom-in" onClick={() => setLightboxAsset(a)}/>
                    ) : a.mime_type === "application/pdf" ? (
                      <a href={a.public_url} target="_blank" rel="noreferrer" className="flex items-center justify-center h-32 bg-red-50">
                        <FileText className="h-8 w-8 text-red-500"/>
                      </a>
                    ) : a.mime_type?.startsWith("video/") ? (
                      <video src={a.public_url} className="w-full h-32 object-cover" controls preload="metadata"/>
                    ) : (
                      <div className="flex items-center justify-center h-32 bg-slate-100">
                        <FileText className="h-8 w-8 text-slate-400"/>
                      </div>
                    )}
                    <div className="px-2 py-1.5 text-[11px]">
                      <p className="font-semibold text-slate-900 truncate" title={a.filename}>{a.filename}</p>
                      <div className="flex items-center justify-between gap-1 mt-0.5">
                        <span className="text-[9px] uppercase tracking-wider text-slate-400">{a.asset_type}</span>
                        <span className="text-[9px] text-slate-400">{Math.round((a.size_bytes||0)/1024)} KB</span>
                      </div>
                    </div>
                    <button onClick={() => deleteAsset(a.asset_id)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity text-slate-500 hover:text-red-500" data-testid={`delete-asset-${a.asset_id}`}>
                      <X className="h-3 w-3"/>
                    </button>
                  </div>
                ))}
              </div>
            )}
            {/* Lightbox */}
            {lightboxAsset && (
              <Dialog open={!!lightboxAsset} onOpenChange={(o) => { if (!o) setLightboxAsset(null); }}>
                <DialogContent className="max-w-4xl">
                  <DialogHeader><DialogTitle className="text-sm">{lightboxAsset.filename}</DialogTitle></DialogHeader>
                  <img src={lightboxAsset.public_url} alt={lightboxAsset.filename} className="w-full h-auto rounded"/>
                </DialogContent>
              </Dialog>
            )}
          </div>
        )}

        {tab === "uploads" && (
          <div className="space-y-2" data-testid="uploads-list">
            {uploads.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No uploads yet — add content in the "Add Content" tab</p>}
            {uploads.map(u => (
              <div key={u.upload_id} className="flex items-start gap-2 bg-slate-50 rounded-lg p-2.5" data-testid={`upload-${u.upload_id}`}>
                <div className="flex-shrink-0 mt-0.5">
                  {u.source === "file" ? <FileText className="h-4 w-4 text-slate-500" /> :
                   u.source === "link" ? <Link2 className="h-4 w-4 text-blue-500" /> :
                   <Clipboard className="h-4 w-4 text-purple-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{u.filename}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${u.learning_status === "processed" ? "bg-emerald-100 text-emerald-700" : u.learning_status === "pending" || u.learning_status === "processing" ? "bg-amber-100 text-amber-700" : u.learning_status === "link_only" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                      {u.learning_status === "processed" ? "Processed" : u.learning_status === "pending" || u.learning_status === "processing" ? <><Loader2 className="h-2.5 w-2.5 inline animate-spin mr-0.5" />Processing</> : u.learning_status === "link_only" ? "External Link" : u.learning_status === "no_text" ? "No text extracted" : u.learning_status}
                    </span>
                    {u.tags?.content_type && <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{u.tags.content_type}</span>}
                    {u.tags?.topic && u.tags.topic !== "Other" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{u.tags.topic}</span>}
                    {u.tags?.style && u.tags.style !== "Other" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{u.tags.style}</span>}
                    {u.chunk_count > 0 && <span className="text-[10px] text-slate-400">{u.chunk_count} chunks indexed</span>}
                  </div>
                  {u.link_url && <a href={u.link_url} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline flex items-center gap-1 mt-0.5"><ExternalLink className="h-3 w-3" />{u.link_url.substring(0, 60)}</a>}
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  {u.learning_status !== "link_only" && u.learning_status !== "processed" && (
                    <button className="text-[10px] text-blue-600 hover:underline" onClick={() => reprocess(u.upload_id)}>Re-process</button>
                  )}
                  <button className="text-slate-400 hover:text-red-500" onClick={() => deleteUpload(u.upload_id)}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "add" && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-indigo-400 transition">
              <Upload className="h-6 w-6 text-slate-400 mx-auto mb-2" />
              <p className="text-xs text-slate-500 mb-2">Upload PDF / DOCX / PPTX / TXT / Image (max 25MB)</p>
              <input type="file" id="cs-file-upload" className="hidden" data-testid="file-upload-input"
                onChange={e => e.target.files?.[0] && uploadFile(e.target.files[0])}
                accept=".pdf,.docx,.pptx,.txt,.md,.png,.jpg,.jpeg,.webp" />
              <label htmlFor="cs-file-upload">
                <Button size="sm" asChild disabled={uploading}><span>{uploading ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Uploading...</> : "Choose File"}</span></Button>
              </label>
            </div>
            <div>
              <Label className="text-xs">Paste text / notes / briefs</Label>
              <Textarea rows={4} value={pasteTxt} onChange={e => setPasteTxt(e.target.value)} placeholder="Paste draft content, brief, or any text you want REX to learn from..." data-testid="paste-text-input" />
              <Button size="sm" className="mt-1" onClick={pasteTextUpload} disabled={!pasteTxt.trim()} data-testid="paste-text-submit"><Sparkles className="h-3 w-3 mr-1" />Add to Knowledge</Button>
            </div>
            <div>
              <Label className="text-xs">Add external link (Google Doc, Figma, etc.)</Label>
              <div className="flex gap-2">
                <Input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://..." data-testid="link-url-input" />
                <Button size="sm" onClick={addLink} disabled={!linkUrl.trim()}>Add</Button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { setDetailCard(null); setFullCard(null); }}>Close</Button>
        </DialogFooter>

        {/* Rejection Reason Modal */}
        <Dialog open={showRejectModal} onOpenChange={(o) => { if (!o) { setShowRejectModal(false); setRejectReason(""); } }}>
          <DialogContent className="max-w-md" data-testid="reject-modal">
            <DialogHeader><DialogTitle>Send back for rework</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <Label className="text-xs">What needs to change? <span className="text-red-500">*</span></Label>
              <Textarea rows={4} value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="e.g. Carousel needs sharper hook on slide 1, please tighten the close." data-testid="reject-reason-input"/>
              <p className="text-[10px] text-slate-500">This note will be emailed to the designer and stored in the card's audit trail.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowRejectModal(false); setRejectReason(""); }}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={rejectInApp} disabled={rejecting || rejectReason.trim().length < 3} data-testid="confirm-reject-btn">
                {rejecting ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : "Reject & notify designer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}


function ApproverConfigDialog({ open, onClose, approverEmails, setApproverEmails, userIsAdmin }) {
  const [draft, setDraft] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setDraft(approverEmails || []); setNewEmail(""); } }, [open, approverEmails]);

  const addEmail = () => {
    const e = newEmail.trim().toLowerCase();
    if (!e || !e.includes("@")) { toast.error("Enter a valid email"); return; }
    if (draft.includes(e)) { toast.info("Already in list"); return; }
    setDraft([...draft, e]);
    setNewEmail("");
  };
  const removeEmail = (e) => setDraft(draft.filter(x => x !== e));

  const saveList = async () => {
    if (!userIsAdmin) { toast.error("Admin role required to save approver list"); return; }
    setSaving(true);
    const r = await apiCall("/content-studio/approvers", {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ approver_emails: draft }),
    });
    setSaving(false);
    if (r.ok) {
      const data = await r.json();
      setApproverEmails(data.approver_emails || []);
      toast.success(`Saved — ${data.approver_emails.length} approver(s) configured`);
      onClose();
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Failed to save");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="approver-config-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-orange-600"/>Approver List</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-600">Only people on this list can approve cards moving from <strong>Pending Approval</strong> → <strong>Approved</strong>. They'll receive a one-click approve/reject email when approval is requested.</p>

          {!userIsAdmin && <div className="text-[11px] bg-amber-50 border border-amber-200 rounded px-2 py-1.5 text-amber-700">Read-only — admin role required to edit.</div>}

          <div className="space-y-1.5">
            {draft.length === 0 ? (
              <p className="text-xs text-slate-400 italic text-center py-3">No approvers configured yet</p>
            ) : draft.map(e => (
              <div key={e} className="flex items-center justify-between gap-2 bg-slate-50 rounded px-2 py-1.5 text-xs" data-testid={`approver-row-${e}`}>
                <span className="font-mono text-slate-700">{e}</span>
                {userIsAdmin && (
                  <button onClick={() => removeEmail(e)} className="text-slate-400 hover:text-red-500" data-testid={`remove-approver-${e}`}>
                    <X className="h-3.5 w-3.5"/>
                  </button>
                )}
              </div>
            ))}
          </div>

          {userIsAdmin && (
            <div className="flex gap-2 pt-2 border-t">
              <Input className="h-8 text-xs" placeholder="approver@resiligent.com" value={newEmail} onChange={e => setNewEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && addEmail()} data-testid="new-approver-input"/>
              <Button size="sm" onClick={addEmail} data-testid="add-approver-btn">Add</Button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {userIsAdmin && (
            <Button onClick={saveList} disabled={saving} data-testid="save-approvers-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : "Save"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function BrandKitDialog({ open, onClose, userIsAdmin }) {
  const [kit, setKit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (!open) { setPreviewUrl(""); return; }
    apiCall("/content-studio/brand-kit").then(r => r.json()).then(setKit).catch(() => {});
  }, [open]);

  const setColor = (k, v) => setKit(p => ({ ...p, colors: { ...(p?.colors || {}), [k]: v } }));
  const setField = (k, v) => setKit(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!userIsAdmin) { toast.error("Admin required"); return; }
    setSaving(true);
    const r = await apiCall("/content-studio/brand-kit", {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ colors: kit.colors, brand_name: kit.brand_name, brand_tagline: kit.brand_tagline, logo_url: kit.logo_url }),
    });
    setSaving(false);
    if (r.ok) {
      toast.success("Brand kit saved — new posts will use these colors");
      setKit(await r.json());
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Failed");
    }
  };

  const previewSample = async () => {
    // Create a tiny throwaway card, auto-render quote_card, show result, then delete card
    setPreviewing(true);
    const createR = await apiCall("/content-studio/cards", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: "Brand kit preview",
        description: "Preview render",
        card_type: "SOCIAL_POST",
        category: "INTERNAL",
        post_type: "company",
        generated_content: "Great leadership compounds — small decisions made well, made consistently, made together.",
      }),
    });
    if (!createR.ok) { setPreviewing(false); toast.error("Preview failed"); return; }
    const { card_id } = await createR.json();
    const rr = await apiCall(`/content-studio/cards/${card_id}/auto-render`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ strategy: "quote_card" }),
    });
    if (rr.ok) {
      const asset = await rr.json();
      setPreviewUrl(asset.public_url + `&_=${Date.now()}`);
    } else {
      toast.error("Render failed");
    }
    // Cleanup
    await apiCall(`/content-studio/cards/${card_id}`, { method: "DELETE" });
    setPreviewing(false);
  };

  if (!kit) return null;
  const colors = kit.colors || {};

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="brand-kit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><SettingsIcon className="h-4 w-4"/>Brand Kit</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-slate-600">Drives the colours and branding used when Rex auto-renders posts (quote cards, stat cards, text posts). Update once; every future auto-render uses these.</p>

          {!userIsAdmin && <div className="text-[11px] bg-amber-50 border border-amber-200 rounded px-2 py-1.5 text-amber-700">Read-only — admin role required to edit.</div>}

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Brand name</Label><Input disabled={!userIsAdmin} value={kit.brand_name || ""} onChange={e => setField("brand_name", e.target.value)} data-testid="brand-name-input"/></div>
            <div><Label className="text-xs">Tagline</Label><Input disabled={!userIsAdmin} value={kit.brand_tagline || ""} onChange={e => setField("brand_tagline", e.target.value)} data-testid="brand-tagline-input"/></div>
          </div>

          <div>
            <Label className="text-xs mb-2 block">Colour palette</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { k: "primary", l: "Primary" },
                { k: "secondary", l: "Accent" },
                { k: "bg_dark", l: "Dark BG" },
                { k: "bg_light", l: "Light BG" },
                { k: "text_on_dark", l: "Text on dark" },
                { k: "text_on_light", l: "Text on light" },
                { k: "muted", l: "Muted" },
                { k: "accent", l: "Highlight" },
              ].map(({ k, l }) => (
                <div key={k} className="text-[10px]">
                  <div className="flex items-center gap-1">
                    <input type="color" disabled={!userIsAdmin} value={colors[k] || "#000000"} onChange={e => setColor(k, e.target.value)} className="w-7 h-7 rounded border" data-testid={`color-${k}`}/>
                    <span className="font-mono text-slate-500">{colors[k]}</span>
                  </div>
                  <p className="text-slate-500 mt-0.5">{l}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-2 border-t">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs">Preview with sample quote</Label>
              <Button size="sm" variant="outline" onClick={previewSample} disabled={previewing} data-testid="preview-render-btn">
                {previewing ? <Loader2 className="h-3 w-3 animate-spin"/> : "Render sample"}
              </Button>
            </div>
            {previewUrl ? (
              <img src={previewUrl} alt="Brand preview" className="w-full max-w-sm mx-auto rounded border border-slate-200" data-testid="brand-preview-image"/>
            ) : (
              <div className="border-2 border-dashed border-slate-200 rounded p-8 text-center text-[11px] text-slate-400">
                Click "Render sample" to preview a quote card with the current palette.
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {userIsAdmin && (
            <Button onClick={save} disabled={saving} data-testid="save-brand-kit-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : "Save brand kit"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

